/**
 * Service Worker Registration and Setup
 * Handles PWA installation, updates, and offline capabilities
 */

/**
 * Register service worker for PWA functionality
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker not supported in this browser');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('Service Worker registered:', registration);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;

      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker available
            console.log('New service worker available. Please reload to update.');

            // Optionally show update notification to user
            showUpdateNotification();
          }
        });
      }
    });

    // Register background sync if available
    // @ts-ignore - sync API may not be in all browsers
    if ('sync' in registration) {
      try {
        // @ts-ignore
        await registration.sync.register('sync-offline-actions');
        console.log('Background sync registered');
      } catch (error) {
        console.warn('Background sync registration failed:', error);
      }
    }

    // Check for updates periodically (every 30 minutes)
    setInterval(
      () => {
        registration.update();
      },
      30 * 60 * 1000
    );

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Unregister service worker (useful for debugging or cleanup)
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (registration) {
      const success = await registration.unregister();
      console.log('Service Worker unregistered:', success);
      return success;
    }

    return false;
  } catch (error) {
    console.error('Service Worker unregistration failed:', error);
    return false;
  }
}

/**
 * Show update notification to user
 */
function showUpdateNotification(): void {
  // Check if notifications are supported
  if ('Notification' in window) {
    if (Notification.permission === 'granted') {
      new Notification('LWM CRM Update Available', {
        body: 'A new version is available. Please reload to update.',
        icon: '/icons/icon-192x192.png',
        tag: 'app-update',
      });
    }
  }

  // You can also dispatch a custom event for in-app notification
  window.dispatchEvent(
    new CustomEvent('sw-update-available', {
      detail: {
        message: 'A new version is available. Please reload to update.',
      },
    })
  );
}

/**
 * Check if service worker is active
 */
export function isServiceWorkerActive(): boolean {
  return 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
}

/**
 * Force service worker update
 */
export async function updateServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (registration) {
      await registration.update();
      console.log('Service Worker update triggered');
    }
  } catch (error) {
    console.error('Service Worker update failed:', error);
  }
}

/**
 * Skip waiting and activate new service worker immediately
 */
export async function skipWaiting(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();

  if (registration && registration.waiting) {
    // Tell the service worker to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    // Reload page after new SW is activated
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  }
}
